import { useEffect, useRef } from 'react';

export default function ClinicalDiagnosticsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-background-100 py-20 md:py-32 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="reveal opacity-0 mb-4">
              <span className="font-body text-xs font-semibold tracking-widest text-primary-500 uppercase">
                Clinical Diagnostics
              </span>
            </div>
            <h2 className="reveal opacity-0 font-display text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-50 leading-tight mb-6">
              A Paradigm Shift in Healthcare Delivery
            </h2>
            <p className="reveal opacity-0 font-body text-base text-foreground-400 leading-relaxed mb-6">
              Our technology represents a paradigm shift in healthcare delivery, utilizing artificial intelligence to analyze vocal biomarkers — subtle patterns in speech, tone, and acoustic features — to detect and monitor various health conditions.
            </p>
            <p className="reveal opacity-0 font-body text-base text-foreground-400 leading-relaxed mb-8">
              By leveraging machine learning, our non-invasive technology can dramatically shorten diagnostic timelines from years to minutes, reducing healthcare costs through early detection and remote monitoring.
            </p>
            <div className="reveal opacity-0 flex items-center gap-4">
              <a
                href="#platform"
                className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-400 font-body text-sm font-medium transition-colors duration-300 group"
              >
                <span>Learn about our platform</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>

          <div className="reveal opacity-0 grid grid-cols-2 gap-4">
            {[
              {
                icon: 'ri-mic-line',
                title: 'Non-Invasive',
                desc: 'Analysis using everyday devices like smartphones or smart speakers',
              },
              {
                icon: 'ri-timer-line',
                title: 'Rapid Results',
                desc: 'Immediate patient screening from voice samples as short as 45 seconds',
              },
              {
                icon: 'ri-global-line',
                title: 'Universal',
                desc: 'Language-independent models applicable across all languages and cultures',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Clinical Grade',
                desc: 'World\'s first regulatory approved SaMD for depression risk assessment',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-500/30 transition-all duration-500 ease-silk group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 mb-4">
                  <i className={`${item.icon} text-primary-500 text-lg`} />
                </div>
                <h3 className="font-body text-sm font-semibold text-foreground-50 mb-2">
                  {item.title}
                </h3>
                <p className="font-body text-xs text-foreground-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}